<template>
    <div :style="blockStyle" class="block-item box p-0 mt-1" :class="{ 'is-active-timer-block': isTimerActive }" v-bind="$attrs">
        <div class="drag-handle" 
             @mousedown="startDrag">
        </div>
        <div class="block-info columns is-mobile m-0" :style="{ height: '100%', marginTop: gridSpan < 6 ? '-0.9rem !important' : '0' }">
            <div class="column is-1-tablet is-2-mobile is-flex">
                <button class="button is-small is-primary has-text-primary-light" style="align-self: flex-start;" @click="startTimer"
                    v-if="!isTimerActive">
                    <span class="icon is-small">
                        <FontAwesomeIcon :icon="faPlay" />
                    </span>
                </button>
                <button class="button is-small is-link is-pulsating" style="align-self: flex-start;" @click="stopTimer" v-else>
                    <span class="icon is-small">
                        <FontAwesomeIcon :icon="faStop" />
                    </span>
                </button>
            </div>
            <div class="column text-truncate has-text-left"
                 style="padding-top: 0.9rem; cursor: pointer; overflow: hidden;"
                @click="showDetails">
                <template v-if="gridSpan >= 8">
                    <span class="truncated-text has-text-weight-medium">{{ block.title }}</span>
                    <span v-if="projectTagsLabel" class="truncated-text block-meta">{{ projectTagsLabel }}</span>
                    <span v-if="showNotePreview" class="block-note">{{ notePreview }}</span>
                </template>
                <template v-else>
                    <span class="truncated-text">
                        <span class="has-text-weight-medium">{{ block.title }}</span>
                        <span v-if="projectTagsLabel" class="block-meta-inline"> {{ projectTagsLabel }}</span>
                        <span v-if="showNotePreview" class="block-note-inline"> • {{ notePreview }}</span>
                    </span>
                </template>
            </div>
            <div class="column is-2-desktop is-4-mobile has-text-right" @click="showDetails" style="cursor: pointer;">
                <div class="" v-if="isTimerActive && (gridSpan > 7)">
                    <p class="" v-html="formatBoldTimecode(store.elapsedTimeInSeconds)"></p>
                    <p class=""><small v-html="formatRemainingTime(store.remainingTimeInSeconds)"></small></p>
                </div>
                <div class="" v-else-if="isTimerActive">
                    <span class="" v-html="formatBoldTimecode(store.elapsedTimeInSeconds)"></span>
                    <span class="has-text-secondary ml-3" v-html="formatRemainingTime(store.remainingTimeInSeconds)"></span>
                </div>
                <div class="has-text-secondary" v-else>
                    {{ formatDuration(block?.duration) }}
                </div>

            </div>
        </div>
        <div class="progress-container">
            <div class="progress-bar" :style="{ width: `${progressPercentage}%` }" :class="{
                'is-active-timer': isTimerActive && progressPercentage < 100,
                'is-active-timer-complete': isTimerActive && progressPercentage >= 100,
                'is-inactive-timer': !isTimerActive && progressPercentage < 100,
                'is-inactive-timer-complete': !isTimerActive && progressPercentage >= 100,
            }" v-if="isTimerActive || block.duration">
            </div>
        </div>
        <div class="resize-handle" @mousedown.stop="startResize"></div>
    </div>
    <div v-if="isDragging" 
         class="drag-tooltip" 
         :style="{ left: tooltipPosition.x + 'px', top: tooltipPosition.y + 'px' }"
         v-html="tooltipContent">
    </div>
</template>

<script setup>
import { computed, toRefs, ref } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { formatBoldTimecode, formatDuration, formatLocalizedTime, formatRemainingTime } from '../utils/time';

defineOptions({
  inheritAttrs: false
});

const props = defineProps(['block', 'displayStart']); // Define 'block' as a prop to pass block data to the component

const store = useBlocksStore(); // Access the store to handle timer start/stop and task sync

// Destructure block properties from props
const { startDate, endDate, id } = toRefs(props.block);

// Computed style for positioning the block item in the scheduling grid
const blockStyle = computed(() => {
    if (props.displayStart > new Date(props.block.endDate)) {
        return { display: 'none' };
    }
    const minutesFromDisplayStart = (new Date(props.block.startDate) - props.displayStart) / (1000 * 60);
    const startRow = Math.floor(minutesFromDisplayStart / 5) + 1;
    return {
        gridRow: `${startRow} / span ${gridSpan.value}`,
        marginBottom: '-2px' 
    };
});

const gridSpan = computed(() => {
    return (new Date(props.block.endDate) - new Date(props.block.startDate)) / (1000 * 60 * 5);
});

const projectTagsLabel = computed(() => {
    const parts = []
    if (props.block.project) parts.push(`@${props.block.project.name}`)
    if (props.block.tags?.length) parts.push(...props.block.tags.map(t => `#${t.name}`))
    return parts.join(' ')
});

const notePreview = computed(() => {
    if (!props.block.notes) return '';
    const stripped = props.block.notes
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, '$1')
        .replace(/(^|\n)([ \t]*)[-*+]\s+/g, '$1$2• ')
        .replace(/(^|\n)>\s*/g, '$1')
        .replace(/(^|\s)#+\s+/gm, '$1')
        .replace(/\n{2,}/g, '\n')
        .trim();
    return stripped;
});

const showNotePreview = computed(() => !!notePreview.value && gridSpan.value >= 8);

const showDetails = () => {
  store.showModal(props.block.id);
};

const progressPercentage = computed(() => {
    if (!props.block) return 0;

    const totalDurationMs = props.block.endDate - props.block.startDate;
    const elapsedMs = 
        isTimerActive.value ? store.elapsedTimeInSeconds * 1000 : props.block.duration * 1000;

    return Math.min(100, Math.round((elapsedMs / totalDurationMs) * 100));
});

const isDragging = ref(false);
const tooltipContent = ref('');
const tooltipPosition = ref({ x: 0, y: 0 });

// Handle drag functionality for the block
const startDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.value = true;
    
    const startY = event.type.includes('touch') ? 
        event.touches[0].clientY : event.clientY;
    const initialStart = new Date(props.block.startDate);
    const initialEnd = new Date(props.block.endDate);
    const displayStart = props.displayStart;

    const onMove = (e) => {
        e.preventDefault();
        const currentY = e.type.includes('touch') ? 
            e.touches[0].clientY : e.clientY;
        const deltaY = currentY - startY;
        const increment = Math.round(deltaY / 10) * 5;
        const newStart = new Date(initialStart.getTime() + increment * 60000);
        const newEnd = new Date(initialEnd.getTime() + increment * 60000);
        
        if (newStart >= displayStart && newEnd > newStart) {
            props.block.startDate = new Date(newStart);
            props.block.endDate = new Date(newEnd);
            
            // Calculate duration in seconds for tooltip
            const durationSeconds = (newEnd - newStart) / 1000;
            
            // Update tooltip with localized time and duration
            tooltipContent.value = `<b>${props.block.title}</b><br/>${formatLocalizedTime(newStart)} - ${formatLocalizedTime(newEnd)}<br/>${formatDuration(durationSeconds)}`;
            tooltipPosition.value = {
                x: e.clientX + 10,
                y: e.clientY + 10
            };
        }
    };

    const onEnd = () => {
        isDragging.value = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('mouseup', onEnd);
        document.removeEventListener('touchend', onEnd);

        // Update store with final position
        store.updateTimer(props.block.id, {
            startDate: props.block.startDate,
            endDate: props.block.endDate
        });
    };

    // Add all event listeners
    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchend', onEnd);
};

// Handle resize functionality for the block
const startResize = (event) => {
    isDragging.value = true;  // Reuse dragging state for tooltip visibility
    const startY = event.clientY; // Initial mouse Y position
    const initialEnd = new Date(props.block.endDate); // Original block end date

    const onMouseMove = (e) => {
        const deltaY = e.clientY - startY; // Calculate mouse movement
        const increment = Math.round(deltaY / 10) * 5; // Adjust movement in 5-minute increments
        const newEnd = new Date(initialEnd.getTime() + increment * 60000); // Calculate new end time
        if (newEnd > props.block.startDate) {
            props.block.endDate = newEnd; // Ensure end date is after start date
            
            // Calculate duration in seconds for tooltip
            const durationSeconds = (newEnd - props.block.startDate) / 1000;
            
            // Update tooltip with times and duration
            tooltipContent.value = `<b>${props.block.title}</b><br/>${formatLocalizedTime(props.block.startDate)} - ${formatLocalizedTime(newEnd)}<br/>${formatDuration(durationSeconds)}`;
            tooltipPosition.value = {
                x: e.clientX + 10,
                y: e.clientY + 10
            };
        }
    };

    const onMouseUp = () => {
        isDragging.value = false;  // Hide tooltip
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);

        store.updateTimer(props.block.id, { 
            startDate: props.block.startDate, 
            endDate: props.block.endDate 
        }); // Sync task updates to the store
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
};

// Start timer for the block
const startTimer = () => {
    store.startTimer(props.block.id); // Activate timer for this block in the store
};

// Stop timer for the block
const stopTimer = () => {
    store.stopTimer(props.block.id); // Deactivate timer for this block in the store
};

// Check if the timer is active for this block
const isTimerActive = computed(() => store.activeTimer && store.activeTimer.id === props.block.id); // Track active timer
</script>

<style>
/* Block Items for Scheduling */
.block-item {
    position: relative;
    border-radius: 4px;
    text-align: center;
    overflow: hidden;
    /* Prevent content overflow */
    display: flex;
    align-items: center;
    justify-content: center;
    /* cursor: pointer; */
    margin: 0.1rem 0;
    z-index: 11;
    -webkit-user-select: none;  /* Safari */
    -moz-user-select: none;     /* Firefox */
    -ms-user-select: none;      /* IE/Edge */
    user-select: none;          /* Standard syntax */
    touch-action: none; /* Prevent touch scrolling during drag */
    transition: box-shadow 0.3s ease;
}

.block-item.is-active-timer-block {
    box-shadow:
        0 0 8px hsla(var(--bulma-link-h), var(--bulma-link-s), calc(var(--bulma-link-l) + 10%), 0.4),
        0 0 16px hsla(var(--bulma-link-h), var(--bulma-link-s), calc(var(--bulma-link-l) + 10%), 0.25);
}

@media (prefers-color-scheme: dark) {
    .block-item.is-active-timer-block {
        box-shadow:
            0 0 8px hsla(var(--bulma-link-h), var(--bulma-link-s), calc(var(--bulma-link-l) + 15%), 0.5),
            0 0 16px hsla(var(--bulma-link-h), var(--bulma-link-s), calc(var(--bulma-link-l) + 15%), 0.35);
    }
}

@keyframes brightness-pulse {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.3); }
}

.button.is-pulsating {
    animation: brightness-pulse 1.5s ease-in-out infinite;
}

/* Resize Handle at the Bottom of Each Block */
.resize-handle {
    width: 100%;
    height: 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    cursor: ns-resize;
    background: linear-gradient(to bottom, transparent, var(--bulma-link-soft));
    opacity: 0;
    transition: opacity 0.2s;
}

.resize-handle:hover {
    opacity: 0.7;
}

.resize-handle:active {
    cursor: grabbing;
    background: linear-gradient(to bottom, transparent, var(--bulma-link));
    transition: background 0.3s ease;
}

/* Drag Handle at the Top of Each Block */
.drag-handle {
    width: 100%;
    height: 10px;
    position: absolute;
    top: 0;
    left: 0;
    cursor: grab;
    opacity: 0;
    transition: opacity 0.2s;
    background: linear-gradient(to top, transparent, var(--bulma-link-soft));
    touch-action: none; /* Prevent touch scrolling during drag */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}

.drag-handle:hover {
    opacity: 0.7;
}

.drag-handle:active {
    cursor: grabbing;
    background: linear-gradient(to top, transparent, var(--bulma-link));
    transition: background 0.3s ease;
}

.block-info {
    width: 100%;
    /* padding: 0.5rem; */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;  
    user-select: none;
}

.progress-container {
    position: absolute;
    bottom: 0;
    /* Above resize handle */
    left: 0;
    width: 100%;
    height: 4px;
    background-color: var(--bulma-background);
}

.progress-bar {
    height: 100%;
    transition: width 0.5s ease;
}

.progress-bar.is-active-timer {
    background-color: var(--bulma-link);
}

.progress-bar.is-active-timer-complete {
    background-color: var(--bulma-success);
    opacity: 0.85;
}

.progress-bar.is-inactive-timer {
    background-color: var(--bulma-primary-soft);
}

.progress-bar.is-inactive-timer-complete {
    background-color: var(--bulma-success-soft);
    opacity: 0.7;
}

.text-truncate {
    min-width: 0; /* Required for text-overflow to work in flex containers */
}

.truncated-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    display: block;
}

.block-meta {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-top: -2px;
}

.block-meta-inline {
    font-size: 0.75rem;
    opacity: 0.6;
    margin-left: 6px;
}

.block-note,
.block-note-inline {
    font-size: 0.75rem;
    color: var(--bulma-text-weak);
}

.block-note {
    display: block;
    line-height: 1.2;
    max-height: 3.6em;
    margin-top: 2px;
    white-space: pre-line;
    overflow: hidden;
    word-break: break-word;
    width: 100%;
}

.drag-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: pre-line;
}
</style>
